import msBaseApi from "../api/msBaseApi";
import { format, parseISO } from "date-fns";
import { toZonedTime, fromZonedTime } from "date-fns-tz";
import { toast } from "sonner";

// Get user's timezone
const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Original CalendarEvent type (as received from API)
export interface CalendarEvent {
  ExternalId?: string; // Optional external ID for integration
  id: string;
  subject: string;
  bodyPreview?: string;
  body?: {
    contentType: "Text" | "HTML";
    content: string;
  };
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  isAllDay?: boolean;
  organizer: {
    emailAddress: {
      name: string;
      address: string;
    };
  };
  location?: {
    displayName: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      countryOrRegion?: string;
    };
  };
  isOnlineMeeting?: boolean;
  onlineMeetingProvider?:
    | "teamsForBusiness"
    | "skypeForBusiness"
    | "skypeForConsumer";
  onlineMeeting?: {
    joinUrl: string;
  };
  attendees?: Array<{
    emailAddress: {
      name: string;
      address: string;
    };
    type?: "required" | "optional" | "resource";
    status?: {
      response: string;
      time: string;
    };
  }>;
  allowNewTimeProposals?: boolean;
  transactionId?: string;
  extensions?: Array<{
    "@odata.type": "microsoft.graph.openTypeExtension";
    extensionName: string;
    workItemNo?: string;
    [key: string]: unknown;
  }>;
  webLink?: string; // Direct link to open event in Outlook Web
}

// Enhanced Calendar Event with serializable string dates
export interface CalendarEventWithLocalTime extends CalendarEvent {
  // Store as ISO strings instead of Date objects for Redux serialization
  localStartTime: string; // ISO string of local start time
  localEndTime: string; // ISO string of local end time
  duration: number; // Duration in minutes (serializable)
  isToday: boolean; // Boolean (serializable)
  isUpcoming: boolean; // Boolean (serializable)
  isOngoing: boolean; // Boolean (serializable)
  isPast: boolean; // Boolean (serializable)
}

// Utility functions for time transformation
export const transformCalendarEventTimes = (
  event: CalendarEvent
): CalendarEventWithLocalTime => {
  // All-day events arrive as midnight-to-midnight in UTC date form; treating
  // them as timestamps would shift them into the wrong hours, so use the date
  // part as a local calendar date instead (end date is exclusive)
  if (event.isAllDay) {
    const startDatePart = event.start.dateTime.slice(0, 10);
    const endDatePart = event.end.dateTime.slice(0, 10);
    const localStartTime = new Date(`${startDatePart}T00:00:00`);
    const localEndTime = new Date(`${endDatePart}T00:00:00`);

    const duration = Math.round(
      (localEndTime.getTime() - localStartTime.getTime()) / (1000 * 60)
    );

    const now = new Date();
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    return {
      ...event,
      localStartTime: `${startDatePart}T00:00:00.000`,
      localEndTime: `${endDatePart}T00:00:00.000`,
      duration,
      isToday:
        localStartTime <= todayStart && todayStart < localEndTime,
      isUpcoming: localStartTime > now,
      isOngoing: localStartTime <= now && localEndTime > now,
      isPast: localEndTime < now,
    };
  }

  let startUtc: Date;
  let endUtc: Date;

  try {
    // Handle different datetime formats from Microsoft Graph
    const startDateString = event.start.dateTime.includes("Z")
      ? event.start.dateTime
      : event.start.dateTime + "Z";

    const endDateString = event.end.dateTime.includes("Z")
      ? event.end.dateTime
      : event.end.dateTime + "Z";

    startUtc = new Date(startDateString);
    endUtc = new Date(endDateString);

    // Fallback: if the above doesn't work, try parseISO
    if (isNaN(startUtc.getTime())) {
      startUtc = parseISO(event.start.dateTime);
    }
    if (isNaN(endUtc.getTime())) {
      endUtc = parseISO(event.end.dateTime);
    }
  } catch (error) {
    console.warn(`Failed to parse datetime for event ${event.id}:`, error);
    startUtc = new Date();
    endUtc = new Date(startUtc.getTime() + 3600000);
  }

  // Convert to local timezone
  const localStartTime = toZonedTime(startUtc, "Europe/Paris");
  const localEndTime = toZonedTime(endUtc, "Europe/Paris");

  // Calculate duration in minutes
  const duration = Math.round(
    (localEndTime.getTime() - localStartTime.getTime()) / (1000 * 60)
  );

  // Calculate time-based booleans
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

  const isToday = localStartTime >= todayStart && localStartTime < todayEnd;
  const isUpcoming = localStartTime > now;
  const isOngoing = localStartTime <= now && localEndTime > now;
  const isPast = localEndTime < now;

  return {
    ...event,
    // Store as ISO strings for Redux serialization
    localStartTime: format(localStartTime, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
    localEndTime: format(localEndTime, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
    duration,
    isToday,
    isUpcoming,
    isOngoing,
    isPast,
  };
};

// Helper functions to work with the serialized dates in components
export const getLocalStartTime = (event: CalendarEventWithLocalTime): Date => {
  return new Date(event.localStartTime);
};

export const getLocalEndTime = (event: CalendarEventWithLocalTime): Date => {
  return new Date(event.localEndTime);
};

// Helper function to format local time for display
export const formatLocalTime = (
  event: CalendarEventWithLocalTime,
  options?: Intl.DateTimeFormatOptions
): string => {
  const localTime = getLocalStartTime(event);
  return localTime.toLocaleString("sv-SE", options);
};

export const formatLocalTimeRange = (
  event: CalendarEventWithLocalTime
): string => {
  const startTime = getLocalStartTime(event);
  const endTime = getLocalEndTime(event);

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
  };

  return `${startTime.toLocaleTimeString(
    "sv-SE",
    timeOptions
  )} - ${endTime.toLocaleTimeString("sv-SE", timeOptions)}`;
};

export const formatLocalDate = (event: CalendarEventWithLocalTime): string => {
  const localTime = getLocalStartTime(event);
  return localTime.toLocaleDateString("sv-SE", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Helper function to transform arrays of events
export const transformCalendarEvents = (
  events: CalendarEvent[]
): CalendarEventWithLocalTime[] => {
  return events.map(transformCalendarEventTimes);
};

export interface CreateEventData {
  subject: string;
  body?: {
    contentType: "Text" | "HTML";
    content: string;
  };
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  location?: {
    displayName: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      countryOrRegion?: string;
    };
  };
  attendees?: Array<{
    emailAddress: {
      name: string;
      address: string;
    };
    type?: "required" | "optional" | "resource";
  }>;
  isOnlineMeeting?: boolean;
  onlineMeetingProvider?:
    | "teamsForBusiness"
    | "skypeForBusiness"
    | "skypeForConsumer";
  allowNewTimeProposals?: boolean;
  transactionId?: string;
  extensions?: Array<{
    "@odata.type": "microsoft.graph.openTypeExtension";
    extensionName: string;
    workItemNo?: string;
    [key: string]: unknown;
  }>;
}

export interface CalendarDateRange {
  startDateTime: Date | string;
  endDateTime: Date | string;
  attendeeEmail?: string;
}

// Helper function to format dates for Microsoft Graph API (ensures UTC)
const formatDateForGraphApi = (date: Date | string): string => {
  if (typeof date === "string") {
    return date;
  }

  const utcDate = fromZonedTime(date, USER_TIMEZONE);
  return utcDate.toISOString();
};
export const calendarApi = msBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCalendarEvents: builder.query<
      CalendarEventWithLocalTime[],
      CalendarDateRange | void
    >({
      query: (dateRange) => {
        const queryParams: Record<string, string | number> = {
          $select:
            "id,subject,bodyPreview,organizer,start,end,isAllDay,location,isOnlineMeeting,onlineMeeting,attendees,responseStatus,type,onlineMeetingProvider,calendar,webLink",
          $orderby: "start/dateTime",
          // Hämta många fler händelser för att säkerställa att vi får alla
          $top: 1000,
        };

        if (dateRange) {
          const startDateStr = formatDateForGraphApi(dateRange.startDateTime);
          const endDateStr = formatDateForGraphApi(dateRange.endDateTime);
          // För calendarView använd StartDateTime och EndDateTime istället för $filter
          queryParams.StartDateTime = startDateStr;
          queryParams.EndDateTime = endDateStr;
        }

        return {
          url: "/me/calendarView",
          params: queryParams,
        };
      },
      transformResponse: (response: { value: CalendarEvent[] }) => {
        console.log(
          `Received ${response.value.length} calendar events from API`
        );
        // Transform all events to include local time
        return transformCalendarEvents(response.value);
      },
      providesTags: ["Calendar"],
    }),

    getCalendarEventById: builder.query<CalendarEventWithLocalTime, string>({
      query: (eventId) => ({
        url: `/me/calendar/events/${eventId}`,
        params: {
          $select: [
            "id",
            "subject",
            "bodyPreview",
            "body",
            "organizer",
            "start",
            "end",
            "isAllDay",
            "location",
            "isOnlineMeeting",
            "onlineMeeting",
            "onlineMeetingProvider",
            "attendees",
            "responseStatus",
            "type",
            "allowNewTimeProposals",
            "transactionId",
            "webLink",
          ].join(","),
        },
      }),
      transformResponse: (response: CalendarEvent) => {
        // Transform single event to include local time
        return transformCalendarEventTimes(response);
      },
      providesTags: (result, error, eventId) => [
        { type: "Calendar", id: eventId },
        { type: "Calendar", id: "LIST" },
      ],
    }),

    getCalendarEventsByIds: builder.query<
      CalendarEventWithLocalTime[],
      string[]
    >({
      query: (eventIds) => {
        const requests = eventIds.map((id, index) => ({
          id: index.toString(),
          method: "GET",
          url: `/me/calendar/events/${id}?$select=id,subject,bodyPreview,body,organizer,start,end,isAllDay,location,isOnlineMeeting,onlineMeeting,onlineMeetingProvider,attendees,responseStatus,type`,
        }));

        return {
          url: "/$batch",
          method: "POST",
          body: {
            requests: requests,
          },
        };
      },
      transformResponse: (response: {
        responses: Array<{
          id: string;
          status: number;
          body: CalendarEvent | { error: { code: string; message: string } };
        }>;
      }) => {
        const successfulEvents = response.responses
          .filter((r) => r.status === 200 && !("error" in r.body))
          .map((r) => r.body as CalendarEvent);

        // Transform all successful events
        return transformCalendarEvents(successfulEvents);
      },
      providesTags: (result, error, eventIds) => [
        ...eventIds.map((id) => ({ type: "Calendar" as const, id })),
        { type: "Calendar", id: "LIST" },
      ],
    }),

    createCalendarEvent: builder.mutation<
      CalendarEventWithLocalTime,
      CreateEventData & { workItemNo?: string }
    >({
      query: ({ workItemNo, ...eventData }) => {
        const body = { ...eventData };

        // Ensure dates are in UTC for the API
        if (body.start?.dateTime) {
          body.start.dateTime = formatDateForGraphApi(body.start.dateTime);
          body.start.timeZone = "UTC";
        }
        if (body.end?.dateTime) {
          body.end.dateTime = formatDateForGraphApi(body.end.dateTime);
          body.end.timeZone = "UTC";
        }

        if (workItemNo) {
          body.extensions = [
            ...(body.extensions ?? []),
            {
              "@odata.type": "microsoft.graph.openTypeExtension",
              extensionName: "com.kraftvyn.workitem",
              workItemNo,
            },
          ];
        }

        return {
          url: "/me/calendar/events",
          method: "POST",
          body,
        };
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Mötesbokning skickad.");
        } catch {
          toast.error(
            "Något gick fel vid skapandet av mötesbokningen. Kontrollera din outlook och försök igen."
          );
        }
      },
      transformResponse: (response: CalendarEvent) => {
        return transformCalendarEventTimes(response);
      },
      invalidatesTags: ["Calendar"],
    }),

    updateCalendarEvent: builder.mutation<
      CalendarEventWithLocalTime,
      { eventId: string; eventData: Partial<CreateEventData> }
    >({
      query: ({ eventId, eventData }) => {
        const body = { ...eventData };

        // Ensure dates are in UTC for the API
        if (body.start?.dateTime) {
          body.start.dateTime = formatDateForGraphApi(body.start.dateTime);
          body.start.timeZone = "UTC";
        }
        if (body.end?.dateTime) {
          body.end.dateTime = formatDateForGraphApi(body.end.dateTime);
          body.end.timeZone = "UTC";
        }

        return {
          url: `/me/calendar/events/${eventId}`,
          method: "PATCH",
          body,
        };
      },
      transformResponse: (response: CalendarEvent) => {
        return transformCalendarEventTimes(response);
      },
      invalidatesTags: ["Calendar"],
    }),

    deleteCalendarEvent: builder.mutation<void, string>({
      query: (eventId) => ({
        url: `/me/calendar/events/${eventId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, eventId) => [
        { type: "Calendar", id: eventId },
        { type: "Calendar", id: "LIST" },
      ],
    }),

    getCalendarEventsByAttendee: builder.query<
      CalendarEventWithLocalTime[],
      { attendeeEmails: string | string[]; dateRange?: CalendarDateRange }
    >({
      query: ({ attendeeEmails, dateRange }) => {
        const queryParams: Record<string, string | number> = {
          $select:
            "id,subject,bodyPreview,organizer,start,end,location,isOnlineMeeting,onlineMeeting,attendees,responseStatus,type,onlineMeetingProvider,calendar",
          $orderby: "start/dateTime",
          $top: 1000,
        };

        // Convert to array if single email is provided
        const emails = Array.isArray(attendeeEmails) ? attendeeEmails : [attendeeEmails];
        
        // Validate that at least one email is provided
        if (emails.length === 0) {
          throw new Error("At least one email address must be provided");
        }

        // Build filter with OR conditions for multiple emails
        const filterConditions = emails
          .filter(email => email && email.trim()) // Remove empty strings
          .map(email => {
            // Basic email escaping for safety
            const escapedEmail = email.replace(/'/g, "''");
            return `attendees/any(a:a/emailAddress/address eq '${escapedEmail}')`;
          })
          .join(' or ');

        if (!filterConditions) {
          throw new Error("No valid email addresses provided");
        }

        queryParams.$filter = filterConditions;

        let endpoint = "/me/events";
        
        // If date range is provided, use calendarView with date parameters
        if (dateRange) {
          endpoint = "/me/calendarView";
          const startDateStr = formatDateForGraphApi(dateRange.startDateTime);
          const endDateStr = formatDateForGraphApi(dateRange.endDateTime);
          queryParams.StartDateTime = startDateStr;
          queryParams.EndDateTime = endDateStr;
        }

        return {
          url: endpoint,
          params: queryParams,
        };
      },
      transformResponse: (response: { value: CalendarEvent[] }) => {
        console.log(
          `Received ${response.value.length} calendar events for attendee from API`
        );
        return transformCalendarEvents(response.value);
      },
      providesTags: [{ type: "Calendar", id: "ATTENDEE_LIST" }],
    }),
  }),
});

export const {
  useGetCalendarEventsQuery,
  useGetCalendarEventByIdQuery,
  useGetCalendarEventsByIdsQuery,
  useGetCalendarEventsByAttendeeQuery,
  useLazyGetCalendarEventByIdQuery,
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation,
  useDeleteCalendarEventMutation,
} = calendarApi;
